import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlarmClock,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  Gift,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const STEPS = [
  { id: 'basic', title: 'Basic pay', short: 'Basic', icon: DollarSign },
  { id: 'overtime', title: 'Overtime', short: 'OT', icon: Clock },
  { id: 'deductions', title: 'Deductions', short: 'Deduct', icon: AlarmClock },
  { id: 'extras', title: 'Bonus & tax', short: 'Extras', icon: Gift },
];

function StepRail({ step, onJump }) {
  return (
    <div className="border-b border-gray-100 px-6 pb-5 pt-2 sm:px-8">
      <div className="space-y-3 sm:hidden">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>
            Step {step + 1} of {STEPS.length}
          </span>
          <span className="font-medium text-gray-800">{STEPS[step].title}</span>
        </div>
        <div className="flex gap-2">
          {STEPS.map((_, i) => (
            <div
              key={STEPS[i].id}
              className={cn(
                'h-2 flex-1 rounded-full transition-colors',
                i <= step ? 'bg-indigo-600' : 'bg-gray-200'
              )}
            />
          ))}
        </div>
      </div>

      <ol className="hidden sm:grid grid-cols-4 gap-3">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const active = i === step;
          const done = i < step;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => onJump(i)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors',
                  active
                    ? 'border-indigo-700 bg-indigo-700 text-white shadow-sm'
                    : done
                      ? 'border-gray-300 bg-white text-gray-800 hover:border-gray-500'
                      : 'border-gray-200 bg-white text-gray-400'
                )}
              >
                <span
                  className={cn(
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-semibold',
                    active
                      ? 'bg-white/15'
                      : done
                        ? 'bg-indigo-50 text-indigo-800'
                        : 'bg-gray-50'
                  )}
                >
                  {done ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
                </span>
                <span className="min-w-0 truncate text-sm font-medium">{s.short}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const hintClass =
  'rounded-2xl border border-gray-200 bg-gray-50/80 px-4 py-3.5 text-sm leading-relaxed text-gray-600';
const fieldClass = 'space-y-2.5';
const gridClass = 'grid grid-cols-1 gap-5 sm:grid-cols-2';
const cardClass = 'space-y-4 rounded-2xl border border-gray-200 p-4 sm:p-5';

export default function SalarySetupWizard({
  open,
  staffName,
  loading,
  salaryData,
  setSalaryData,
  currencies = [],
  getCurrencyInfo,
  onSave,
  onDelete,
  onCancel,
}) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  const patch = (partial) => setSalaryData((prev) => ({ ...prev, ...partial }));
  const patchNested = (key, partial) =>
    setSalaryData((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...partial },
    }));

  const goNext = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const goBack = () => setStep((s) => Math.max(s - 1, 0));

  const currencyLabel = (() => {
    if (!getCurrencyInfo) return salaryData.currency;
    const curr = getCurrencyInfo(salaryData.currency);
    return `${curr.flag} ${curr.code} (${curr.symbol})`;
  })();

  return (
    <div className="flex min-h-0 max-h-[90vh] flex-1 flex-col">
      <DialogHeader className="shrink-0 space-y-2 px-6 pb-4 pt-7 text-left sm:px-8">
        <DialogTitle className="flex items-center gap-2.5 text-2xl font-semibold tracking-tight">
          <DollarSign className="h-6 w-6 shrink-0 text-indigo-600" />
          <span className="truncate">Salary — {staffName}</span>
        </DialogTitle>
        <DialogDescription className="text-sm leading-relaxed text-gray-500">
          Set pay, overtime, deductions, and bonuses in a few steps
        </DialogDescription>
      </DialogHeader>

      <div className="shrink-0">
        <StepRail step={step} onJump={setStep} />
      </div>

      <div className="min-h-[340px] flex-1 overflow-y-auto px-6 py-6 sm:min-h-[380px] sm:px-8 sm:py-7">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
          </div>
        ) : (
          <>
            {step === 0 && (
              <div className="space-y-6">
                <div className={hintClass}>Base pay and how often they get paid.</div>
                <div className={gridClass}>
                  <div className={fieldClass}>
                    <Label>Salary type</Label>
                    <Select
                      value={salaryData.salary_type}
                      onValueChange={(v) => patch({ salary_type: v })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="hourly">Hourly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={fieldClass}>
                    <Label>Payment cycle</Label>
                    <Select
                      value={salaryData.payment_cycle}
                      onValueChange={(v) => patch({ payment_cycle: v })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="bi_weekly">Bi-Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className={fieldClass}>
                    <Label>Base salary</Label>
                    <Input
                      className="h-11"
                      type="number"
                      value={salaryData.base_salary}
                      onChange={(e) => patch({ base_salary: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className={fieldClass}>
                    <Label>Currency</Label>
                    <Select
                      value={salaryData.currency}
                      onValueChange={(v) => patch({ currency: v })}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue>{currencyLabel}</SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-[280px]">
                        {currencies.map((curr) => (
                          <SelectItem key={curr.code} value={curr.code}>
                            {curr.flag} {curr.code} — {curr.name} ({curr.symbol})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6">
                <div className={hintClass}>Extra hours after the shift — optional.</div>
                <div className={fieldClass}>
                  <Label>Overtime pay</Label>
                  <Select
                    value={salaryData.overtime_pay_enabled ? 'yes' : 'no'}
                    onValueChange={(v) => patch({ overtime_pay_enabled: v === 'yes' })}
                  >
                    <SelectTrigger className="h-11 max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {salaryData.overtime_pay_enabled && (
                  <div className={gridClass}>
                    <div className={fieldClass}>
                      <Label>Rate (multiplier)</Label>
                      <Input
                        className="h-11"
                        type="number"
                        step="0.1"
                        value={salaryData.overtime_rate}
                        onChange={(e) =>
                          patch({
                            overtime_rate: e.target.value === '' ? '' : parseFloat(e.target.value),
                          })
                        }
                        placeholder="1.5"
                      />
                    </div>
                    <div className={fieldClass}>
                      <Label>Calculation</Label>
                      <Select
                        value={salaryData.overtime_calculation}
                        onValueChange={(v) => patch({ overtime_calculation: v })}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="after_shift">After shift hours</SelectItem>
                          <SelectItem value="after_hours">After X hours/day</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className={fieldClass}>
                      <Label>
                        {salaryData.overtime_calculation === 'after_hours'
                          ? 'Pay threshold (hours/day)'
                          : 'Pay threshold (hours)'}
                      </Label>
                      <Input
                        className="h-11"
                        type="number"
                        value={salaryData.overtime_threshold_hours}
                        onChange={(e) =>
                          patch({ overtime_threshold_hours: parseFloat(e.target.value) || 8 })
                        }
                        placeholder="8"
                      />
                      <p className="text-sm text-gray-500">For overtime pay calculation only.</p>
                    </div>
                    <div className={fieldClass}>
                      <Label>Max OT hours (auto check-out)</Label>
                      <Input
                        className="h-11"
                        type="number"
                        min="0"
                        max="12"
                        step="0.5"
                        value={salaryData.max_overtime_hours ?? 4}
                        onChange={(e) =>
                          patch({
                            max_overtime_hours:
                              e.target.value === '' ? '' : parseFloat(e.target.value),
                          })
                        }
                        placeholder="4"
                      />
                      <p className="text-sm text-gray-500">
                        Hours after shift end before auto check-out. 0 = at shift end. Max 12.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div className={hintClass}>Late penalties and how absences affect pay.</div>

                <div className={cardClass}>
                  <p className="text-base font-semibold text-gray-900">Late penalty</p>
                  <div className={gridClass}>
                    <div className={fieldClass}>
                      <Label>Enabled</Label>
                      <Select
                        value={salaryData.late_penalty?.enabled ? 'yes' : 'no'}
                        onValueChange={(v) => patchNested('late_penalty', { enabled: v === 'yes' })}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {salaryData.late_penalty?.enabled && (
                      <>
                        <div className={fieldClass}>
                          <Label>Type</Label>
                          <Select
                            value={salaryData.late_penalty?.penalty_type || 'fixed'}
                            onValueChange={(v) => patchNested('late_penalty', { penalty_type: v })}
                          >
                            <SelectTrigger className="h-11">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">Fixed amount</SelectItem>
                              <SelectItem value="per_minute">Per minute</SelectItem>
                              <SelectItem value="per_hour">Per hour</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className={`${fieldClass} sm:col-span-2`}>
                          <Label>Amount ({salaryData.currency})</Label>
                          <Input
                            className="h-11 max-w-sm"
                            type="number"
                            value={salaryData.late_penalty?.amount || 0}
                            onChange={(e) =>
                              patchNested('late_penalty', {
                                amount: parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className={cardClass}>
                  <p className="text-base font-semibold text-gray-900">Absence deduction</p>
                  <p className="text-sm text-gray-500">
                    Per day / hour: unpaid absent days. Custom: extra penalty on top.
                  </p>
                  <div className={gridClass}>
                    <div className={fieldClass}>
                      <Label>Rule</Label>
                      <Select
                        value={salaryData.absence_deduction?.rule || 'per_day'}
                        onValueChange={(v) => patchNested('absence_deduction', { rule: v })}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="per_day">Per day (unpaid)</SelectItem>
                          <SelectItem value="per_hour">Per hour (unpaid)</SelectItem>
                          <SelectItem value="custom">Custom amount (extra)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {salaryData.absence_deduction?.rule === 'custom' && (
                      <div className={fieldClass}>
                        <Label>Custom amount ({salaryData.currency})</Label>
                        <Input
                          className="h-11"
                          type="number"
                          value={salaryData.absence_deduction?.custom_amount || 0}
                          onChange={(e) =>
                            patchNested('absence_deduction', {
                              custom_amount: parseFloat(e.target.value) || 0,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div className={hintClass}>Allowances, bonuses, tax, and notes.</div>

                <div className={gridClass}>
                  <div className={fieldClass}>
                    <Label>Fixed monthly allowance</Label>
                    <Input
                      className="h-11"
                      type="number"
                      value={salaryData.bonus_allowances?.fixed_monthly || 0}
                      onChange={(e) =>
                        patchNested('bonus_allowances', {
                          fixed_monthly: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div className={fieldClass}>
                    <Label>Performance bonus</Label>
                    <Input
                      className="h-11"
                      type="number"
                      value={salaryData.bonus_allowances?.performance_bonus || 0}
                      onChange={(e) =>
                        patchNested('bonus_allowances', {
                          performance_bonus: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div className={fieldClass}>
                    <Label>Attendance bonus</Label>
                    <Input
                      className="h-11"
                      type="number"
                      value={salaryData.bonus_allowances?.attendance_bonus || 0}
                      onChange={(e) =>
                        patchNested('bonus_allowances', {
                          attendance_bonus: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                  <div className={fieldClass}>
                    <Label>Travel / food</Label>
                    <Input
                      className="h-11"
                      type="number"
                      value={salaryData.bonus_allowances?.travel_food_allowance || 0}
                      onChange={(e) =>
                        patchNested('bonus_allowances', {
                          travel_food_allowance: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>

                <div className={cardClass}>
                  <p className="text-base font-semibold text-gray-900">Tax</p>
                  <div className={gridClass}>
                    <div className={fieldClass}>
                      <Label>Tax enabled</Label>
                      <Select
                        value={salaryData.tax_settings?.enabled ? 'yes' : 'no'}
                        onValueChange={(v) => patchNested('tax_settings', { enabled: v === 'yes' })}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="yes">Yes</SelectItem>
                          <SelectItem value="no">No</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {salaryData.tax_settings?.enabled && (
                      <>
                        <div className={fieldClass}>
                          <Label>Country</Label>
                          <Input
                            className="h-11"
                            value={salaryData.tax_settings?.country || ''}
                            onChange={(e) =>
                              patchNested('tax_settings', { country: e.target.value })
                            }
                            placeholder="e.g. USA, Pakistan"
                          />
                        </div>
                        <div className={`${fieldClass} sm:col-span-2`}>
                          <Label>Percentage (%)</Label>
                          <Input
                            className="h-11 max-w-sm"
                            type="number"
                            step="0.1"
                            value={salaryData.tax_settings?.percentage || 0}
                            onChange={(e) =>
                              patchNested('tax_settings', {
                                percentage: parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className={fieldClass}>
                  <Label>Compliance notes</Label>
                  <Input
                    className="h-11"
                    value={salaryData.compliance_notes}
                    onChange={(e) => patch({ compliance_notes: e.target.value })}
                    placeholder="Optional notes…"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <DialogFooter className="mt-auto flex-col gap-3 border-t bg-gray-50/80 px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:space-x-0">
        <div className="flex w-full gap-2.5 sm:w-auto">
          <Button
            type="button"
            variant="outline"
            className="h-11 flex-1 sm:flex-none"
            onClick={goBack}
            disabled={step === 0 || loading}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-11 w-11 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700 sm:hidden"
            onClick={onDelete}
            disabled={loading}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex w-full flex-col-reverse gap-2.5 sm:w-auto sm:flex-row sm:items-center">
          <Button
            type="button"
            variant="ghost"
            className="hidden h-11 text-red-600 hover:bg-red-50 hover:text-red-700 sm:inline-flex"
            onClick={onDelete}
            disabled={loading}
          >
            <Trash2 className="mr-1.5 h-4 w-4" />
            Delete
          </Button>
          <Button type="button" variant="outline" className="h-11" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          {step < STEPS.length - 1 ? (
            <>
              <Button
                type="button"
                variant="secondary"
                className="h-11 w-full sm:w-auto"
                onClick={onSave}
                disabled={loading}
              >
                Save
              </Button>
              <Button
                type="button"
                className="h-11 w-full sm:min-w-[140px] sm:w-auto"
                onClick={goNext}
                disabled={loading}
              >
                Continue
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </>
          ) : (
            <Button
              type="button"
              className="h-11 w-full bg-indigo-600 hover:bg-indigo-700 sm:min-w-[140px] sm:w-auto"
              onClick={onSave}
              disabled={loading}
            >
              Save setup
            </Button>
          )}
        </div>
      </DialogFooter>
    </div>
  );
}
